import BackendService from 'BackendService';
import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  ALL_PUSH_NOTIFICATION_FIELDS,
  type PushNotificationConfigField,
} from '../../../constants/pushNotificationCategories';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import { getDropdownSx } from '../../../utils/ModelOptions';
import {
  broadcastGlobalConfigUpdate,
  focusFieldByHash,
  getDropdownOptions,
} from './globalConfigAdminUtils';
import { isNotificationHashField, veraSubTabForHash } from './globalConfigFieldConfig';

export function useGlobalConfigAdmin() {
  const location = useLocation();
  const [config, setConfig] = React.useState<GlobalConfiguration | null>(null);
  const [originalConfig, setOriginalConfig] = React.useState<GlobalConfiguration | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [tab, setTab] = React.useState('general');
  const [generalTab, setGeneralTab] = React.useState('feature');
  const [veraTab, setVeraTab] = React.useState('proposals');
  const [advancedTab, setAdvancedTab] = React.useState('ai-models');
  const [dirtyFields, setDirtyFields] = React.useState<Set<string>>(new Set());

  const dropdownSx = getDropdownSx();

  React.useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;
    const veraSubTab = veraSubTabForHash(hash);
    if (veraSubTab) {
      setTab('vera');
      setVeraTab(veraSubTab);
      focusFieldByHash(hash);
    } else if (isNotificationHashField(hash)) {
      setTab('general');
      setGeneralTab('notifications');
      focusFieldByHash(hash);
    }
  }, [location.hash]);

  const load = React.useCallback(() => {
    setLoading(true);
    new BackendService()
      .getGlobalConfiguration()
      .then((cfg) => {
        setConfig(cfg);
        setOriginalConfig(cfg);
        setDirtyFields(new Set());
      })
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const markDirty = React.useCallback(
    (field: string, value: unknown) => {
      if (!originalConfig) return;
      setDirtyFields((prev) => {
        const updated = new Set(prev);
        const originalValue = originalConfig[field as keyof GlobalConfiguration];
        if (originalValue !== value) {
          updated.add(field);
        } else {
          updated.delete(field);
        }
        return updated;
      });
    },
    [originalConfig],
  );

  const handleChange = React.useCallback(
    (field: string, value: unknown) => {
      setConfig((c) => (c ? { ...c, [field]: value } : c));
      markDirty(field, value);
    },
    [markDirty],
  );

  const setAllPushTypes = React.useCallback(
    (enabled: boolean) => {
      if (!config) return;
      const updates = Object.fromEntries(
        ALL_PUSH_NOTIFICATION_FIELDS.map((field) => [field, enabled]),
      ) as Record<PushNotificationConfigField, boolean>;
      setConfig((c) => (c ? { ...c, ...updates } : c));
      ALL_PUSH_NOTIFICATION_FIELDS.forEach((field) => {
        markDirty(field, enabled);
      });
    },
    [config, markDirty],
  );

  const revertFieldChange = React.useCallback(
    (field: string) => {
      if (!originalConfig) return;
      setConfig((c) =>
        c ? { ...c, [field]: originalConfig[field as keyof GlobalConfiguration] } : c,
      );
      setDirtyFields((prev) => {
        const updated = new Set(prev);
        updated.delete(field);
        return updated;
      });
    },
    [originalConfig],
  );

  const resolveDropdownOptions = React.useCallback(
    (fieldKey: string) => getDropdownOptions(config, fieldKey),
    [config],
  );

  const save = React.useCallback(() => {
    if (!config) return;
    setSaving(true);
    new BackendService()
      .updateGlobalConfiguration(config)
      .then(() => new BackendService().getGlobalConfiguration())
      .then((cfg) => {
        setConfig(cfg);
        setOriginalConfig(cfg);
        setDirtyFields(new Set());
        broadcastGlobalConfigUpdate(cfg);
      })
      .finally(() => setSaving(false));
  }, [config]);

  const isDirty = React.useCallback(
    (fields: readonly string[]) => fields.some((f) => dirtyFields.has(f)),
    [dirtyFields],
  );

  return {
    config,
    originalConfig,
    loading,
    saving,
    tab,
    setTab,
    generalTab,
    setGeneralTab,
    veraTab,
    setVeraTab,
    advancedTab,
    setAdvancedTab,
    dirtyFields,
    dropdownSx,
    handleChange,
    setAllPushTypes,
    revertFieldChange,
    resolveDropdownOptions,
    save,
    isDirty,
  };
}

export type UseGlobalConfigAdminReturn = ReturnType<typeof useGlobalConfigAdmin>;
